import React from 'react'
import { Box, Typography, styled } from '@mui/material'
import { Avatar } from '@/design-system'
import CustomTimeAgo from '@/components/CustomTimeAgo'
import { ActivityTypeUtil, NotificationItem, NotificationKind } from '@/types/notification'
import { convertToGMT } from '@/utils/helper'

// Solid fills (matching the notifications mock) so the badge reads clearly against
// any avatar photo/gradient behind it — the previous translucent color-mix tints
// were too faint to register as a background at all next to a colorful emoji.
const KIND_STYLE: Record<NotificationKind, { icon: string; bg: string; ink: string }> = {
  like: { icon: '♥', bg: 'var(--ds-accent-deep-a)', ink: '#fff' },
  comment: { icon: '💬', bg: 'var(--ds-accent-deep-b)', ink: '#fff' },
  follow: { icon: '＋', bg: '#01dafe', ink: '#063' },
  request: { icon: '👋', bg: '#01dafe', ink: '#063' },
  upload: { icon: '↑', bg: '#ffcf7d', ink: '#5c3d00' },
  share: { icon: '⇢', bg: 'var(--ds-accent-deep-a)', ink: '#fff' },
}

const Row = styled(Box, { shouldForwardProp: (p) => p !== '$unread' })<{ $unread?: boolean }>(({ $unread }) => ({
  display: 'flex',
  alignItems: 'flex-start',
  gap: 14,
  padding: '16px 18px',
  cursor: 'pointer',
  position: 'relative',
  transition: `background var(--ds-duration-fast) var(--ds-ease)`,
  background: $unread ? 'color-mix(in srgb, var(--ds-accent-deep-b) 5%, transparent)' : 'transparent',
  '&:hover': { background: $unread ? 'color-mix(in srgb, var(--ds-accent-deep-b) 8%, transparent)' : 'var(--ds-hover)' },
  '&::before': $unread
    ? {
        content: '""',
        position: 'absolute',
        left: 0,
        top: 0,
        bottom: 0,
        width: 3,
        background: 'var(--ds-gradient-spectrum, var(--ds-ink))',
      }
    : undefined,
}))

const AvWrap = styled(Box)({ position: 'relative', flexShrink: 0 })

const KindBadge = styled(Box)({
  position: 'absolute',
  bottom: -3,
  right: -3,
  width: 19,
  height: 19,
  borderRadius: '50%',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  fontSize: 10,
  boxShadow: '0 0 0 2px var(--ds-canvas)',
})

const Body = styled(Box)({ flex: 1, minWidth: 0, paddingTop: 1 })

const Txt = styled(Typography)({
  fontSize: 13.5,
  lineHeight: 1.5,
  color: 'var(--ds-ink-muted)',
  '& b': { fontWeight: 640, color: 'var(--ds-ink)' },
})

const Quote = styled(Box)({
  marginTop: 7,
  padding: '9px 12px',
  borderRadius: 9,
  background: 'var(--ds-hover)',
  fontSize: 12.5,
  color: 'var(--ds-ink-muted)',
  fontStyle: 'italic',
  lineHeight: 1.5,
})

const Time = styled(Box)({
  marginTop: 5,
  fontFamily: 'var(--ds-font-mono)',
  fontSize: 10,
  letterSpacing: '0.04em',
  color: 'var(--ds-ink-subtle)',
})

export interface NotificationRowProps {
  item: NotificationItem
  onClick: (item: NotificationItem) => void
}

export function NotificationRow({ item, onClick }: NotificationRowProps) {
  const kind = ActivityTypeUtil.getKind(item.eventType)
  const kindStyle = KIND_STYLE[kind]
  const activityText = ActivityTypeUtil.getActivityText(item.eventType)
  const isTopicRelated = ActivityTypeUtil.isTopicRelatedEvent(item.eventType)
  const commentText = ActivityTypeUtil.getCommentText(item)
  const displayName = item.sourceUserFullName || 'Someone'
  const initial = displayName[0]?.toUpperCase() ?? '?'

  return (
    <Row $unread={!item.isRead} onClick={() => onClick(item)}>
      <AvWrap>
        <Avatar
          src={item.profilePicThumbURL}
          sx={{
            width: 38,
            height: 38,
            fontSize: 13,
            fontWeight: 700,
            background: 'var(--ds-gradient-spectrum, var(--ds-ink))',
            color: '#fff',
          }}
        >
          {initial}
        </Avatar>
        <KindBadge sx={{ background: kindStyle.bg, color: kindStyle.ink }}>{kindStyle.icon}</KindBadge>
      </AvWrap>

      <Body>
        <Txt>
          <b>{displayName}</b> {activityText}
          {isTopicRelated && item.secondName && (
            <>
              {' '}
              <b>{item.secondName}</b>
            </>
          )}
        </Txt>
        {commentText && <Quote>&ldquo;{commentText}&rdquo;</Quote>}
        <Time>
          <CustomTimeAgo date={convertToGMT(item.eventDateTime)} />
        </Time>
      </Body>
    </Row>
  )
}
